import React, { useCallback } from 'react';
import { Col, Row } from 'antd';
import { gql, useNavigate, useParams, useLocation } from '@common/hooks';
import { Offers } from './offers';
import { ApplicationHeader } from './application-header';
import { AppContent, Tabs } from '@common/components';
import ApplicationProfileComments from 'works/pages/organization/applications/application-profile-comments';
import { ApplicationSummary } from '@applicant-flow/components';
import moment from 'moment';

type Props = {
  // TODO: Remove explicit any type once we add types to the query
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  application: any;
};

export function ApplicationProfile({ application }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: currentTabKey } = useParams<{ tab: 'summary' | 'notes' }>();

  const handleTabChange = useCallback(
    (key: string) => {
      const url = location.pathname.replace(`/${currentTabKey}`, `/${key}`);

      navigate(url);
    },
    [navigate, location.pathname, currentTabKey]
  );

  const defaultTab = 'summary';
  const tab = currentTabKey || defaultTab;

  return (
    <>
      <AppContent>
        <ApplicationHeader application={application} />
        <Row gutter={16}>
          <Col lg={{ span: 18 }} xs={{ span: 24 }}>
            <Tabs
              activeKey={tab}
              defaultActiveKey={defaultTab}
              onTabClick={handleTabChange}
            >
              <Tabs.Pane tab="Summary" key="summary">
                <ApplicationSummary
                  summary={application.worksProfile.profileSummary}
                  availableToStartAt={moment(
                    application.availableToStartAt
                  ).format('MM/DD/YYYY')}
                  timeoffDescription={application.timeoffDescription}
                  interviewAvailability={application.interviewAvailability}
                  employmentType={application.employmentType}
                  displayApplicationInformation={true}
                  submittalPacket={application.worksSubmittalPacket}
                  applicationId={application.id}
                  applicationState={application.state}
                />
              </Tabs.Pane>
              <Tabs.Pane tab="Notes" key="notes">
                <ApplicationProfileComments
                  applicationId={application.id}
                  commentable={application.commentable}
                  canComment={application.canComment}
                  comments={application.comments}
                />
              </Tabs.Pane>
            </Tabs>
          </Col>

          <Col lg={{ span: 6 }} xs={{ span: 24 }}>
            <Offers records={application.activeOffers} />
          </Col>
        </Row>
      </AppContent>
    </>
  );
}

ApplicationProfile.fragments = {
  profileInfo: gql`
    fragment StaffingRequestApplicationProfileInfo on StaffingRequestApplication {
      id
      state
      stateChangedAt
      createdAt
      availableToStartAt
      timeoffDescription
      interviewAvailability
      employmentType
      rejectionReason
      rejectionReasonDescription
      rejectionDetails
      doNotRehire
      doNotRehireComment

      supplier {
        id
        name
      }

      organization {
        id
        name
      }

      worksProfile {
        id
        firstName
        lastName
        phone
        email
        profileSummary
        ...ApplicationHeaderWorksProfileFragment
      }

      canComment {
        value
      }
      commentable
      comments {
        id
        author {
          id
          name
        }
        contentAsText
        updatedAt
      }
    }

    ${ApplicationHeader.fragments.worksProfile}
  `,
};
